import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Settings, 
  FileText, 
  Map as MapIcon, 
  Users, 
  LogOut, 
  Globe, 
  Shield, 
  Image, 
  TrendingUp, 
  Wand2, 
  Gavel, 
  Book, 
  Palette, 
  UserPlus, 
  Calendar, 
  Trophy, 
  Award,
  Star
} from 'lucide-react';

interface AdminSidebarProps {
  logout: () => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ logout }) => {
  const navigate = useNavigate();

  const navGroups = [
    {
      title: 'Sistema & Dashboard',
      items: [
        { icon: LayoutDashboard, label: 'Geral', path: '/admin/dashboard' },
        { icon: Settings, label: 'Configurações Site', path: '/admin/site-settings' },
        { icon: UserPlus, label: 'Cadastros', path: '/admin/registrations' },
        { icon: Shield, label: 'Administradores', path: '/admin/security' },
      ]
    },
    {
      title: 'Conteúdo & Galeria',
      items: [
        { icon: Image, label: 'Arquivos Visuais', path: '/admin/gallery' },
        { icon: FileText, label: 'Novidades (Blog)', path: '/admin/content' },
        { icon: TrendingUp, label: 'Notas de Update', path: '/admin/updates' },
      ]
    },
    {
      title: 'Mundo & Wiki',
      items: [
        { icon: Book, label: 'Config Biblioteca', path: '/admin/library-settings' },
        { icon: Wand2, label: 'Mods do Servidor', path: '/admin/mods' },
        { icon: Gavel, label: 'Regulamentos', path: '/admin/rules' },
        { icon: MapIcon, label: 'Mapa Interativo', path: '/admin/map' },
      ]
    },
    {
      title: 'Comunidade',
      items: [
        { icon: Palette, label: 'Catálogo de Skins', path: '/admin/skins' },
        { icon: Calendar, label: 'Eventos', path: '/admin/events' },
        { icon: Trophy, label: 'Ranking & Feitos', path: '/admin/ranking' },
        { icon: Award, label: 'Gerenciar Apoiadores', path: '/admin/supporters' },
        { icon: Star, label: 'Patrocinadores', path: '/admin/sponsors' },
        { icon: Globe, label: 'Hytale Sync', path: '/admin/hytale-sync' },
      ]
    }
  ];

  return (
    <aside className="w-56 bg-[#0F172A]/60 backdrop-blur-md border-r border-white/[0.05] flex flex-col h-screen sticky top-0 shrink-0">
      <div className="p-8 shrink-0">
        <h1 className="text-lg font-semibold text-white tracking-[0.15em] flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
          <Shield size={18} className="text-[#14B8A6]" />
          ADMIN
        </h1>
        <p className="text-[9px] text-[#14B8A6]/50 uppercase tracking-[0.4em] mt-2 font-medium">Controle Nucleus</p>
      </div>

      <nav className="flex-1 overflow-y-auto px-4 py-2 custom-scrollbar space-y-8">
        {navGroups.map((group, idx) => (
          <div key={idx}>
            <h3 className="px-4 mb-4 text-[9px] font-semibold text-slate-300/50 uppercase tracking-[0.3em]">
              {group.title}
            </h3>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-4 py-2 rounded-xl transition-all duration-300 group relative
                    ${isActive 
                      ? 'text-white bg-[#14B8A6]/10 border border-[#14B8A6]/10' 
                      : 'text-slate-400 hover:text-white hover:bg-white/[0.04] border border-transparent'}
                  `}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <div className="absolute left-0 w-1 h-4 bg-[#14B8A6] rounded-r-full" />
                      )}
                      <item.icon size={14} className={`${isActive ? 'text-[#14B8A6]' : 'opacity-60 group-hover:opacity-100'} transition-all`} />
                      <span className="text-[11px] font-medium tracking-wide">{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="p-4 border-t border-white/[0.05] mt-auto bg-[#0F172A]/40 backdrop-blur-md">
        <button 
          onClick={() => navigate('/')} 
          className="flex items-center gap-3 px-4 py-2 text-[10px] text-slate-400 hover:text-white w-full transition-all uppercase tracking-widest font-semibold rounded-xl hover:bg-white/5"
        >
          <Globe size={14} />
          <span>Ver Site</span>
        </button>
        <button 
          onClick={logout} 
          className="flex items-center gap-3 px-4 py-2 text-[10px] text-red-500/60 hover:text-red-400 w-full transition-all uppercase tracking-widest font-semibold rounded-xl hover:bg-red-500/5"
        >
          <LogOut size={14} />
          <span>Sair do Painel</span>
        </button>
      </div>
    </aside>
  );
};
